import { c as _c } from "react/compiler-runtime";
import { BulkActionsBar, type TableKey, type TableSelectionMode, type TableSortDescriptor } from '@systemeio/components';
import React, { useRef, type JSX } from 'react';
import { EmptyListState, type EmptyStateProps } from 'shared/components/empty-states';
import useMediaQuery from 'shared/hooks/use-media-query';
import { DataWithPaginationInterface } from 'shared/types/pagination-interface';
import { twJoin, twMerge } from 'tailwind-merge';
import { SortDirectionEnum } from '../enums';
import { ColumnDefinitionInterface } from '../types/column-interface';
import { calculateTableTotalWidth } from '../utils';
import type { TableRowClassName } from '../utils/table-row-helpers';
import { DesktopView } from './desktop-view';
import { MobileView } from './mobile-view';
import { Pagination, PaginationProps } from './pagination';
import { TabletView } from './tablet-view';
export type BaseTableProps<T> = {
  data: DataWithPaginationInterface<T> | undefined;
  columns: ColumnDefinitionInterface<T>[];
  /** Drops the surrounding card so a host surface — a popover popup, for instance — owns it. */
  bare?: boolean;
  selectedKeys?: Iterable<TableKey>;
  selectionMode?: TableSelectionMode;
  allowSelectAll?: boolean;
  selectedAll?: boolean;
  bulkActions?: React.ReactNode;
  bulkActionsOpen?: boolean;
  testAttributePostfix?: string;
  noHeader?: boolean;
  emptyStateProps?: EmptyStateProps;
  isSortable?: boolean;
  footerAdditionalElement?: JSX.Element;
  rowClassName?: TableRowClassName<T>;
  getRowId?: (row: T) => string | number;
  /** Replaces the labelled column stack inside the mobile card with a hand-composed layout. */
  renderMobileRow?: (row: T) => React.ReactNode;
  /** Optional tablet layout; otherwise preserves the mobile renderer or default mobile cards. */
  renderTabletRow?: (row: T) => React.ReactNode;
  onRowClick?: (instance: T) => void;
  onBulkActionsOpenChange?: (open: boolean) => void;
  onSelectionChange?: (keys: Set<TableKey>) => void;
  onSelectAll?: () => void;
  onClearSelection?: () => void;
} & Partial<PaginationProps<T>>;
export function BaseTable(t0) {
  const $ = _c(70);
  const {
    data,
    columns,
    bare,
    selectedKeys,
    selectionMode,
    allowSelectAll,
    selectedAll,
    bulkActions,
    bulkActionsOpen,
    limiter,
    paginator,
    testAttributePostfix,
    noHeader,
    emptyStateProps,
    isSortable,
    footerAdditionalElement,
    rowClassName,
    renderMobileRow,
    renderTabletRow,
    getRowId,
    onRowClick,
    onBulkActionsOpenChange,
    onSelectionChange,
    onSelectAll,
    onClearSelection
  } = t0;
  const {
    isDesktop,
    isTablet,
    isMobile
  } = useMediaQuery();
  const tableRef = useRef(null);
  let t1;
  let totalWidth;
  let visibleColumns;
  if ($[0] !== columns) {
    visibleColumns = columns.filter(_temp);
    totalWidth = calculateTableTotalWidth(visibleColumns);
    t1 = visibleColumns.find(_temp2);
    $[0] = columns;
    $[1] = t1;
    $[2] = totalWidth;
    $[3] = visibleColumns;
  } else {
    t1 = $[1];
    totalWidth = $[2];
    visibleColumns = $[3];
  }
  const sortedColumn = t1;
  let t2;
  if ($[4] !== sortedColumn) {
    t2 = sortedColumn?.sort?.sortOrder ? {
      column: sortedColumn.key,
      direction: sortedColumn.sort.sortOrder === SortDirectionEnum.Asc ? "ascending" : "descending"
    } : undefined;
    $[4] = sortedColumn;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const sortDescriptor = t2;
  let t3;
  if ($[6] !== visibleColumns) {
    t3 = nextSortDescriptor => {
      const nextSortedColumn = visibleColumns.find(column_1 => column_1.key === nextSortDescriptor.column);
      nextSortedColumn?.sort?.onSortOrderChange();
    };
    $[6] = visibleColumns;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleSortChange = t3;
  const isLoading = data === undefined;
  const hasRows = !!data && data.items.length > 0;
  const shouldKeepTableVisible = !!paginator?.pagination.startFromId;
  const isBulkActionsOpen = !!bulkActions && !!bulkActionsOpen;
  const showSelectionFacility = !isLoading && !!selectionMode && selectionMode !== "none";
  const t4 = showSelectionFacility ? selectionMode : undefined;
  let t5;
  if ($[8] !== onClearSelection || $[9] !== onSelectAll || $[10] !== onSelectionChange || $[11] !== selectedKeys || $[12] !== t4) {
    t5 = {
      selectedKeys,
      selectionMode: t4,
      onSelectionChange,
      onSelectAll,
      onClearSelection
    };
    $[8] = onClearSelection;
    $[9] = onSelectAll;
    $[10] = onSelectionChange;
    $[11] = selectedKeys;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const selectionProps = t5;
  let t6;
  if ($[14] !== data || $[15] !== getRowId || $[16] !== isSortable || $[17] !== limiter || $[18] !== onRowClick || $[19] !== rowClassName || $[20] !== testAttributePostfix || $[21] !== totalWidth || $[22] !== visibleColumns) {
    t6 = {
      data,
      limiter,
      visibleColumns,
      totalWidth,
      testAttributePostfix,
      isSortable,
      rowClassName,
      getRowId,
      onRowClick
    };
    $[14] = data;
    $[15] = getRowId;
    $[16] = isSortable;
    $[17] = limiter;
    $[18] = onRowClick;
    $[19] = rowClassName;
    $[20] = testAttributePostfix;
    $[21] = totalWidth;
    $[22] = visibleColumns;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const rowsProps = t6;
  const view = isDesktop ? "desktop" : isTablet && renderTabletRow ? "tablet" : "mobile";
  const t7 = !bare && "border-border-100 bg-bg-primary rounded-xl border";
  let t8;
  if ($[24] !== t7) {
    t8 = twMerge("overflow-hidden", t7);
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const viewClassName = t8;
  if (!isLoading && !hasRows && !shouldKeepTableVisible) {
    const t9 = !bare && "border-border-100 bg-bg-primary rounded-xl border";
    let t10;
    if ($[26] !== t9) {
      t10 = twMerge("flex w-full overflow-hidden", t9);
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== emptyStateProps) {
      t11 = <EmptyListState {...emptyStateProps} />;
      $[28] = emptyStateProps;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== t10 || $[31] !== t11) {
      t12 = <div className={t10}>{t11}</div>;
      $[30] = t10;
      $[31] = t11;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    let t13;
    if ($[33] !== footerAdditionalElement) {
      t13 = footerAdditionalElement && <div className="mt-3 flex">{footerAdditionalElement}</div>;
      $[33] = footerAdditionalElement;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    let t14;
    if ($[35] !== t12 || $[36] !== t13) {
      t14 = <>{t12}{t13}</>;
      $[35] = t12;
      $[36] = t13;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    return t14;
  }
  const t9 = !!data;
  let t10;
  if ($[38] !== allowSelectAll || $[39] !== columns || $[40] !== handleSortChange || $[41] !== noHeader || $[42] !== renderMobileRow || $[43] !== renderTabletRow || $[44] !== rowsProps || $[45] !== selectedAll || $[46] !== selectionProps || $[47] !== sortDescriptor || $[48] !== view) {
    t10 = view === "desktop" ? <DesktopView {...rowsProps} selectionProps={selectionProps} columns={columns} noHeader={noHeader} selectedAll={selectedAll} allowSelectAll={allowSelectAll} sortDescriptor={sortDescriptor} onSortChange={handleSortChange} /> : view === "tablet" && renderTabletRow ? <TabletView {...rowsProps} selectionProps={selectionProps} renderRow={renderTabletRow} /> : <MobileView {...rowsProps} selectionProps={selectionProps} allowSelectAll={allowSelectAll} renderRow={renderMobileRow} />;
    $[38] = allowSelectAll;
    $[39] = columns;
    $[40] = handleSortChange;
    $[41] = noHeader;
    $[42] = renderMobileRow;
    $[43] = renderTabletRow;
    $[44] = rowsProps;
    $[45] = selectedAll;
    $[46] = selectionProps;
    $[47] = sortDescriptor;
    $[48] = view;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== t10 || $[51] !== view || $[52] !== viewClassName) {
    t11 = <div className={viewClassName} data-table-view={view}>{t10}</div>;
    $[50] = t10;
    $[51] = view;
    $[52] = viewClassName;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  if ($[54] !== data || $[55] !== footerAdditionalElement || $[56] !== getRowId || $[57] !== limiter || $[58] !== paginator) {
    t12 = (footerAdditionalElement || paginator && limiter) && <div className={twJoin("mt-3 flex empty:hidden", footerAdditionalElement ? "justify-between" : "justify-end")}>{footerAdditionalElement}{paginator && limiter && <Pagination tableRef={tableRef} getRowId={getRowId} limiter={limiter} paginator={paginator} data={data} />}</div>;
    $[54] = data;
    $[55] = footerAdditionalElement;
    $[56] = getRowId;
    $[57] = limiter;
    $[58] = paginator;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== bulkActions || $[61] !== isBulkActionsOpen || $[62] !== isMobile || $[63] !== onBulkActionsOpenChange) {
    t13 = bulkActions && <BulkActionsBar open={isBulkActionsOpen} onOpenChange={onBulkActionsOpenChange} closeProps={isMobile ? {
      className: "hidden"
    } : undefined}>{bulkActions}</BulkActionsBar>;
    $[60] = bulkActions;
    $[61] = isBulkActionsOpen;
    $[62] = isMobile;
    $[63] = onBulkActionsOpenChange;
    $[64] = t13;
  } else {
    t13 = $[64];
  }
  let t14;
  if ($[65] !== t11 || $[66] !== t12 || $[67] !== t13 || $[68] !== t9) {
    t14 = <div className="flex w-full flex-col" ref={tableRef} data-test="table" data-test-table-is-loaded={t9}>{t11}{t12}{t13}</div>;
    $[65] = t11;
    $[66] = t12;
    $[67] = t13;
    $[68] = t9;
    $[69] = t14;
  } else {
    t14 = $[69];
  }
  return t14;
}
function _temp2(column_0) {
  return !!column_0.sort?.sortOrder;
}
function _temp(column) {
  return column.shouldRender !== false;
}